<script lang="ts">
  import type { ModalPositions, ModalSizes } from './Modal.types';

  export * from './Modal.types';

  export interface ModalProps {
    /**
     * Hides the "close" button
     */
    hideClose?: boolean;

    /**
     * Opens the modal when truthy; hides the modal when falsy.
     * @deprecated Use `isOpen` instead
     */
    open?: boolean;

    /**
     * Opens the modal when truthy; hides the modal when falsy.
     */
    isOpen?: boolean;

    /**
     * Use v-model:is-open or :is-open instead of :model-value and v-model.
     * @deprecated
     */
    modelValue?: boolean;

    /**
     * Sets a preset max-width on the modal.
     * Options: default (648px), narrow (360px), wide (960px)
     */
    size?: ModalSizes;

    /**
     * Should the modal be scrollable within the content area. This prop is treated as `true` when the `position` prop is set to "left" or "right".
     */
    scrollable?: boolean;

    /**
     * Gives the modal body have a light gray background
     */
    contrast?: boolean;

    /**
     * Text to display in the modal header
     */
    title?: string;

    /**
     * Disables the default padding in the modal body.
     */
    disableBodyPadding?: boolean;

    /**
     * The position on the screen to display the modal.
     */
    position?: ModalPositions;

    /**
     * Hide the header. Typically used with the featuredContent slot to display a graphic and create a "promo" modal.
     */
    hideHeader?: boolean;

    /**
     * Add classes to the close button. This can be used with the hideHeader prop and featuredContent slot to
     * accommodate images with different color backgrounds.
     */
    closeButtonColorClass?: string;

    /**
     * Prevents the modal from being dismissed by clicking the backdrop or pressing the escape key.
     * Example: There are in-flight api requests and you do not want the modal to close until they are done.
     */
    preventDismiss?: boolean;
  }
</script>

<script setup lang="ts">
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, onBeforeUnmount, ref, useSlots, watch } from 'vue';

  import { FOCUS_ELEMENTS_SELECTOR } from '../../constants';
  import { t } from '../../locale';
  import Backdrop from '../Backdrop/Backdrop.vue';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';

  defineOptions({ name: 'll-modal' });

  const props = withDefaults(defineProps<ModalProps>(), {
    hideClose: false,
    open: false,
    isOpen: false,
    modelValue: false,
    size: 'medium',
    scrollable: false,
    contrast: false,
    title: '',
    position: 'center',
    hideHeader: false,
    closeButtonColorClass: 'text-white/50',
    preventDismiss: false,
  });

  const emit = defineEmits<{
    /**
     * @deprecated Use the `update:is-open` event instead
     */
    (e: 'update:open', isOpen?: boolean): void;
    (e: 'update:is-open', isOpen?: boolean): void;
    (e: 'dismiss'): void;
  }>();

  const slots = useSlots();

  const rootRef = ref<HTMLElement>();
  const lastExternalFocusedElement = ref<HTMLElement | null>(null);
  const focusElements = ref<HTMLElement[]>([]);
  const firstFocusElement = ref<HTMLElement>();
  const lastFocusElement = ref<HTMLElement>();
  const initialPageScrollingElementStyle = ref({ height: '', overflow: '' });
  const headerId = uniqueId('modal-header-');
  const hasFooterContent = computed(() => !!slots.actions || !!slots.footer);
  const isModalOpen = computed(() => props.open || props.isOpen);
  const isDrawer = computed(() => props.position === 'left' || props.position === 'right');

  let isFocusTrapAttached = false;

  function getPageScrollingElement() {
    return (document.scrollingElement || document.body) as HTMLElement;
  }

  watch(
    isModalOpen,
    () => {
      if (typeof document === 'undefined') {
        return;
      }
      toggleHelpWidgetLauncher(isModalOpen.value ? 'hide' : 'show');

      if (isModalOpen.value) {
        const active = document.activeElement;
        lastExternalFocusedElement.value = active instanceof HTMLElement ? active : null;
      } else {
        if (isFocusTrapAttached) {
          document.removeEventListener('keydown', handleTab);
          isFocusTrapAttached = false;
        }

        lastExternalFocusedElement.value?.focus();
      }

      if (isModalOpen.value) {
        Object.assign(initialPageScrollingElementStyle.value, {
          height: getPageScrollingElement().style.height,
          overflow: getPageScrollingElement().style.overflow,
        });
      }

      Object.assign(getPageScrollingElement().style, {
        overflow: isModalOpen.value ? 'hidden' : initialPageScrollingElementStyle.value.overflow, // Prevents page from scrolling when modal is open
        height: isModalOpen.value ? '100%' : initialPageScrollingElementStyle.value.height, // Ensures the backdrop covers the entire page when modal is open; see https://github.com/LeafLink/stash/pull/713#issuecomment-1184602535
      });
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    if (typeof document === 'undefined') {
      return;
    }
    toggleHelpWidgetLauncher('show');

    // In cases where the watchEffect for isModalOpen isn't triggered while closing/nagivating away from modal, this ensures scrolling returns to normal
    Object.assign(getPageScrollingElement().style, {
      overflow: initialPageScrollingElementStyle.value.overflow,
      height: initialPageScrollingElementStyle.value.height,
    });

    // Clear focus trap tab listener
    if (isFocusTrapAttached) {
      document.removeEventListener('keydown', handleTab);
      isFocusTrapAttached = false;
    }

    lastExternalFocusedElement.value?.focus();
  });

  function dismiss() {
    if (props.preventDismiss) {
      return;
    }
    emit('update:open', false);
    emit('update:is-open', false);
    emit('dismiss');
  }

  function handleTab(e: KeyboardEvent) {
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstFocusElement.value) {
        lastFocusElement.value?.focus();
        e.preventDefault();
      } else if (document.activeElement === lastFocusElement.value) {
        firstFocusElement.value?.focus();
        e.preventDefault();
      }
    }
  }

  function setupFocusTrap() {
    if (typeof document === 'undefined' || !rootRef.value || !isModalOpen.value) {
      return;
    }

    rootRef.value.focus();

    focusElements.value = Array.from(rootRef.value.querySelectorAll<HTMLElement>(FOCUS_ELEMENTS_SELECTOR));
    firstFocusElement.value = focusElements.value[0];
    lastFocusElement.value = focusElements.value[focusElements.value.length - 1];

    if (!isFocusTrapAttached) {
      document.addEventListener('keydown', handleTab);
      isFocusTrapAttached = true;
    }
  }

  // Ensure the Tab key cycles through focusable elements within the modal only while it is open.
  watch(rootRef, () => {
    if (!rootRef.value || !isModalOpen.value) {
      return;
    }

    setupFocusTrap();
  });

  /**
   * The customer support "help widget launcher" covers the action buttons in the Modal when the Modal uses a position value of "right" (for drawers).
   */
  function toggleHelpWidgetLauncher(nextState: 'show' | 'hide') {
    if (typeof document === 'undefined') {
      return;
    }
    const launcherElement = document.getElementById('launcher');

    if (!launcherElement || !launcherElement.parentElement) {
      return;
    }

    launcherElement.parentElement.style.display = nextState === 'show' ? 'block' : 'none';
  }
</script>

<template>
  <div
    v-if="isModalOpen"
    ref="rootRef"
    class="stash-modal fixed inset-0"
    :class="{
      'invisible z-behind': !isModalOpen,
      'visible z-modal': isModalOpen,
      'lg:flex lg:flex-col lg:items-center lg:justify-center': props.position === 'center',
      'overflow-y-auto': !props.scrollable && !isDrawer,
      'overflow-y-hidden': isDrawer,
    }"
    data-test="ll-modal"
    tabindex="0"
    @keydown.esc="dismiss"
  >
    <Backdrop class="stash-modal__backdrop" @click.stop="dismiss" />
    <div
      aria-modal="true"
      role="dialog"
      :aria-labelledby="headerId"
      class="stash-modal__dialog relative flex h-screen w-full flex-col lg:shadow-3xl"
      :class="[
        `stash-modal__dialog--size-${props.size}`,
        `stash-modal__dialog--position-${props.position}`,
        {
          'stash-modal__dialog--is-open': isModalOpen,
          'stash-modal__dialog--is-drawer': isDrawer,
          'stash-modal__dialog--is-contrast': props.contrast,
          'stash-modal__dialog--is-scrollable': props.scrollable,
          'lg:w-[360px]': props.size === 'narrow',
          'lg:w-[648px]': props.size === 'medium',
          'lg:w-[960px]': props.size === 'wide',
          'lg:my-0 lg:h-auto lg:max-h-[90vh]': props.position === 'center',
          // absolute causing this to break when items in bottom of container get focus
          'lg:fixed lg:h-screen': isDrawer,
          'lg:left-0': props.position === 'left',
          'lg:right-0': props.position === 'right',
        },
      ]"
      @click.stop
    >
      <header
        v-if="!props.hideHeader"
        data-test="stash-modal__header"
        class="stash-modal__header grid h-12 place-items-center bg-purple-500"
        :class="{ 'lg:rounded-t': !isDrawer }"
      >
        <div class="flex place-items-center">
          <!-- @slot Adds an action to the left side of the header bar. An example usage is a modal with multiple pages and a back button can be inserted here -->
          <slot name="headerAction"></slot>
        </div>

        <h3 v-if="props.title" :id="headerId" class="m-0 flex-1 leading-6 text-white">
          {{ props.title }}
        </h3>

        <Button
          v-if="!props.hideClose"
          icon
          data-test="ll-modal-close"
          :title="t('ll.closeModal')"
          type="button"
          @click="dismiss"
        >
          <Icon class="text-white" name="close" />
        </Button>
      </header>

      <Button
        v-if="!props.hideClose && props.hideHeader"
        class="absolute right-0 top-0 z-10"
        icon
        data-test="ll-modal-close"
        type="button"
        :title="t('ll.closeModal')"
        @click="dismiss"
      >
        <Icon class="drop-shadow-md" name="close" :class="[props.closeButtonColorClass]" />
      </Button>

      <div
        v-if="!!slots['featured-content']"
        class="stash-modal__featured-content relative"
        :class="{
          'rounded-t': props.hideHeader,
        }"
      >
        <slot name="featured-content"></slot>
      </div>

      <div
        class="stash-modal__body flex-1 overflow-y-auto"
        :class="[
          {
            'p-3 lg:p-6': !props.disableBodyPadding,
            'lg:overflow-y-visible': !props.scrollable && !isDrawer,
            'lg:rounded-b': !hasFooterContent && !isDrawer,
            'bg-white': !props.contrast,
            'bg-ice-200': props.contrast,
          },
        ]"
        data-test="stash-modal__body"
      >
        <slot></slot>
      </div>

      <footer
        v-if="hasFooterContent"
        class="stash-modal__footer border-t border-ice-500 bg-ice-100 p-3 lg:p-6"
        :class="{ 'lg:rounded-b': !isDrawer }"
      >
        <!-- @slot Overrides the whole footer section. Used for rendering custom footers with more than 2 actions. If defined, "actions" slot will get ignored. -->
        <slot name="footer">
          <div class="stash-modal__footer__actions flex justify-end">
            <!-- @slot Modal footer actions, supports rendering up to 2 `<Button>` children -->
            <slot name="actions"></slot>
          </div>
        </slot>
      </footer>
    </div>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  :global(:where(.stash-modal) .stash-modal__header) {
    grid-template-columns: 48px 1fr 48px;
  }

  /* Direction lives here (not Tailwind flex-col / lg:flex-row on the template) so utilities don’t fight column-reverse */
  :global(:where(.stash-modal) .stash-modal__footer__actions) {
    flex-direction: column-reverse;
    gap: --spacing(3);
  }

  @media (width >= theme(--breakpoint-lg)) {
    :global(:where(.stash-modal) .stash-modal__footer__actions) {
      flex-direction: row;
      gap: var(--grid-gutter);
    }
  }

  :global(:where(.stash-modal) .stash-modal__featured-content > *) {
    border-radius: inherit;
  }
</style>
